import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  TextField,
  Alert,
  Card,
  CardContent,
  CardActions,
  Divider,
  Stack,
} from '@mui/material';
import {
  Download,
  Assessment,
  Description,
  People,
  Business,
  Work,
  Receipt,
  AccountBalance,
  FileDownload,
  PictureAsPdf,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const AdminReportsPage: React.FC = () => {
  const { user } = useAuth();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Check if user is admin
  if (!user || user.userType !== 'admin') {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Access denied. Admin only.</Alert>
      </Container>
    );
  }

  const handleExport = async (reportType: string, format: 'csv' | 'excel') => {
    try {
      setLoading(`${reportType}-${format}`);
      setError('');
      setSuccess('');

      const params = new URLSearchParams();
      params.append('format', format);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/admin/reports/${reportType}?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Export failed');
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${reportType}-${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'csv'}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) filename = filenameMatch[1];
      }

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccess(`${reportType} exported successfully as ${format.toUpperCase()}`);
      setLoading(null);
    } catch (err: any) {
      setError(err.message || 'Failed to export report');
      setLoading(null);
    }
  };

  const handle1099Generation = async () => {
    try {
      setLoading('1099');
      setError('');
      setSuccess('');
      
      // TODO: Implement 1099-NEC generation
      // This will need to be implemented based on your 1099 requirements
      alert('1099-NEC generation will be available in January 2025. This feature will generate 1099-NEC forms for all providers who earned $600+ in 2024.');
      
      setLoading(null);
    } catch (err: any) {
      setError(err.message || 'Failed to generate 1099 forms');
      setLoading(null);
    }
  };

  const handleDownloadProviderDocuments = async () => {
    try {
      setLoading('documents');
      setError('');
      setSuccess('');
      
      // TODO: Implement ZIP download of provider documents
      // This will need to collect W-9s, insurance documents, agreements
      alert('Provider documents ZIP download will be available soon. This will include W-9s, insurance documents, and agreements for all providers.');
      
      setLoading(null);
    } catch (err: any) {
      setError(err.message || 'Failed to download provider documents');
      setLoading(null);
    }
  };

  const reportCards = [
    {
      id: 'payouts',
      title: 'Payout History',
      description: 'Every dollar paid to providers - date, amount, provider, job details',
      icon: <AccountBalance sx={{ fontSize: 40 }} />,
      priority: 1,
      color: '#1976d2',
    },
    {
      id: 'payments',
      title: 'Payment History',
      description: 'Every dollar customers paid - Stripe ID, date, amount, customer info',
      icon: <Receipt sx={{ fontSize: 40 }} />,
      priority: 2,
      color: '#2e7d32',
    },
    {
      id: 'providers',
      title: 'Provider Data',
      description: 'Name, business info, insurance expiry, verification status',
      icon: <Business sx={{ fontSize: 40 }} />,
      priority: 3,
      color: '#ed6c02',
    },
    {
      id: 'service-requests',
      title: 'Service Request / Job History',
      description: 'All service requests, bids, winner, final price, status changes',
      icon: <Work sx={{ fontSize: 40 }} />,
      priority: 4,
      color: '#9c27b0',
    },
    {
      id: 'customers',
      title: 'Customer Data',
      description: 'Name, address, phone, email, service history',
      icon: <People sx={{ fontSize: 40 }} />,
      priority: 5,
      color: '#d32f2f',
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Compliance & Reports
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Export business records for compliance, auditing, and tax purposes. All data is retained for 7+ years per IRS requirements.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Date Range Filter */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Date Range Filter (Optional)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Leave blank for "All Time" export. Date range applies to Payout History, Payment History, and Service Request History.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Report Export Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {reportCards.map((report) => (
          <Grid item xs={12} md={6} key={report.id}>
            <Card sx={{ height: '100%', borderLeft: `4px solid ${report.color}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ color: report.color, mr: 2 }}>
                    {report.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {report.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Priority {report.priority}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {report.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2, justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Download />}
                  onClick={() => handleExport(report.id, 'csv')}
                  disabled={loading === `${report.id}-csv`}
                >
                  {loading === `${report.id}-csv` ? 'Exporting...' : 'CSV'}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Description />}
                  onClick={() => handleExport(report.id, 'excel')}
                  disabled={loading === `${report.id}-excel`}
                  sx={{ bgcolor: report.color, '&:hover': { bgcolor: report.color, opacity: 0.9 } }}
                >
                  {loading === `${report.id}-excel` ? 'Exporting...' : 'Excel'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Additional Actions */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Additional Actions
        </Typography>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            size="large"
            startIcon={<PictureAsPdf />}
            onClick={handle1099Generation}
            disabled={loading === '1099'}
            fullWidth
            sx={{ justifyContent: 'flex-start', py: 1.5 }}
          >
            {loading === '1099' ? 'Generating...' : 'Generate 1099-NECs for 2025'}
            <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
              Available in January
            </Typography>
          </Button>

          <Button
            variant="outlined"
            size="large"
            startIcon={<FileDownload />}
            onClick={handleDownloadProviderDocuments}
            disabled={loading === 'documents'}
            fullWidth
            sx={{ justifyContent: 'flex-start', py: 1.5 }}
          >
            {loading === 'documents' ? 'Preparing...' : 'Download All Provider Documents as ZIP'}
            <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
              W-9s, Insurance, Agreements
            </Typography>
          </Button>
        </Stack>
      </Paper>

      {/* Compliance Notice */}
      <Alert severity="info" sx={{ mt: 4 }}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          Compliance Notice
        </Typography>
        <Typography variant="body2">
          All transaction records are retained for a minimum of 7 years per IRS requirements. 
          Exports include all data within the selected date range (or all time if no range is specified).
        </Typography>
      </Alert>
    </Container>
  );
};

export default AdminReportsPage;

